import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { Search, Pencil, Shield, User, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { adminAPI, trekAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import useListQueryParams from '../hooks/useListQueryParams';
import { packageDetailSummary } from '../utils/packageDisplayText.js';
import AdminDateRangeFilter from '../components/AdminDateRangeFilter.jsx';
import { PageHeader, FilterBar, EmptyState, StatusChip } from '../components/ui';
import { brand } from '../theme';
import './ModuleAdmin.css';
import './Users.css';

const Users = () => {
  const { isSuperAdmin } = useAuth();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { query, setQuery } = useListQueryParams({ page: '1', search: '', role: '', isActive: '' });
  const search = query.search || '';
  const roleFilter = query.role || '';
  const activeFilter = query.isActive || '';
  const page = Number(query.page || 1);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    role: 'user',
    isActive: true
  });
  const [handlesLeads, setHandlesLeads] = useState(false);
  const [leadTrekIds, setLeadTrekIds] = useState([]);
  const [leadPackageIds, setLeadPackageIds] = useState([]);
  const [leadFromDate, setLeadFromDate] = useState('');
  const [leadToDate, setLeadToDate] = useState('');
  const [leadAllocMode, setLeadAllocMode] = useState('calendar');
  const [leadAllocPresetId, setLeadAllocPresetId] = useState('');
  const [consultantTrekIds, setConsultantTrekIds] = useState([]);
  const [smartfloTokenId, setSmartfloTokenId] = useState('');
  const [presetDialogOpen, setPresetDialogOpen] = useState(false);
  const [newPresetLabel, setNewPresetLabel] = useState('');
  const [newPresetDays, setNewPresetDays] = useState('');

  const limit = 15;

  const { data, isLoading, error } = useQuery(
    ['admin-users', page, search, roleFilter, activeFilter],
    () => adminAPI.getAllUsers({ page, limit, search: search || undefined, role: roleFilter || undefined, isActive: activeFilter === '' ? undefined : activeFilter })
  );
  const { data: rolesData } = useQuery('admin-roles', adminAPI.getRoles, { enabled: isSuperAdmin });
  const roles = rolesData?.data?.data || [];
  const roleNames = Array.from(new Set(['user', 'super_admin', ...roles.map((r) => r.name)]));

  const { data: treksData } = useQuery(
    ['admin-treks-for-lead-allocation'],
    () => trekAPI.list({ page: 1, limit: 500, isActive: true }),
    { enabled: isSuperAdmin }
  );
  const trekOptions = useMemo(() => {
    const list = treksData?.data?.data || [];
    return [...list].sort((a, b) =>
      String(a?.name || '').localeCompare(String(b?.name || ''), undefined, { sensitivity: 'base' })
    );
  }, [treksData]);

  const leadTrekIdsKey = leadTrekIds.join(',');
  const { data: leadOptionsData, isFetching: leadOptionsLoading } = useQuery(
    ['lead-allocation-options', leadTrekIdsKey],
    () => adminAPI.getLeadAllocationOptions(leadTrekIds),
    { enabled: isSuperAdmin && handlesLeads && leadTrekIds.length > 0 }
  );
  const leadPackages = leadOptionsData?.data?.data?.packages || leadOptionsData?.data?.packages || [];

  const { data: leadAllocationData, isFetching: leadAllocationLoading } = useQuery(
    ['user-lead-allocation', editingUser?.id],
    () => adminAPI.getUserLeadAllocation(editingUser.id),
    { enabled: isSuperAdmin && Boolean(editingUser?.id) }
  );

  const { data: consultantTreksData, isFetching: consultantTreksLoading } = useQuery(
    ['user-consultant-treks', editingUser?.id],
    () => adminAPI.getConsultantTreks(editingUser.id),
    { enabled: isSuperAdmin && Boolean(editingUser?.id) }
  );

  const { data: leadPresetsData, refetch: refetchLeadPresets } = useQuery(
    ['lead-alloc-presets', presetDialogOpen],
    () => adminAPI.getLeadAllocPresets({ activeOnly: presetDialogOpen ? 'false' : 'true' }),
    { enabled: isSuperAdmin }
  );
  const leadPresets = leadPresetsData?.data?.data || [];
  const activeLeadPresets = presetDialogOpen
    ? leadPresets.filter((p) => p.isActive !== false)
    : leadPresets;

  const { data: smartfloTokensData, isFetching: smartfloTokensLoading, refetch: refetchSmartfloTokens } = useQuery(
    ['smartflo-c2c-tokens'],
    () => adminAPI.getSmartfloC2cTokens({ sync: '1' }),
    { enabled: isSuperAdmin && Boolean(editingUser?.id), staleTime: 2 * 60 * 1000 }
  );
  const smartfloTokenOptions = smartfloTokensData?.data?.data || [];

  const { data: userSmartfloData, isFetching: userSmartfloLoading } = useQuery(
    ['user-smartflo', editingUser?.id],
    () => adminAPI.getUserSmartfloAssignment(editingUser.id),
    { enabled: isSuperAdmin && Boolean(editingUser?.id) }
  );

  const updateMutation = useMutation(
    ({ id, payload }) => adminAPI.updateUserRoleAndPermissions(id, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-users');
      },
      onError: (err) => {
        showToast({
          message: err.response?.data?.message || 'Failed to update user',
          severity: 'error'
        });
      }
    }
  );
  const deleteUserMutation = useMutation(
    (id) => adminAPI.deleteAdminUser(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-users');
        showToast({ message: 'User deleted.', severity: 'success' });
      },
      onError: (err) => {
        showToast({
          message: err.response?.data?.message || 'Failed to delete user',
          severity: 'error'
        });
      }
    }
  );
  const setLeadAllocationMutation = useMutation(
    ({ userId, body }) => adminAPI.setUserLeadAllocation(userId, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['user-lead-allocation']);
      },
      onError: (err) =>
        showToast({
          message: err.response?.data?.message || 'Failed to save lead allocation',
          severity: 'error'
        })
    }
  );
  const setConsultantTreksMutation = useMutation(
    ({ userId, trekIds }) => adminAPI.setConsultantTreks(userId, trekIds),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['user-consultant-treks']);
        queryClient.invalidateQueries(['consultant-upcoming-treks']);
      },
      onError: (err) =>
        showToast({
          message: err.response?.data?.message || 'Failed to save trek consultant assignment',
          severity: 'error'
        })
    }
  );
  const createPresetMutation = useMutation(
    (body) => adminAPI.createLeadAllocPreset(body),
    {
      onSuccess: () => {
        refetchLeadPresets();
        queryClient.invalidateQueries(['lead-alloc-presets']);
        setNewPresetLabel('');
        setNewPresetDays('');
        showToast({ message: 'Preset created.', severity: 'success' });
      },
      onError: (err) =>
        showToast({
          message: err.response?.data?.message || 'Failed to create preset',
          severity: 'error'
        })
    }
  );
  const updatePresetMutation = useMutation(
    ({ id, body }) => adminAPI.updateLeadAllocPreset(id, body),
    {
      onSuccess: () => {
        refetchLeadPresets();
        queryClient.invalidateQueries(['lead-alloc-presets']);
        showToast({ message: 'Preset updated.', severity: 'success' });
      },
      onError: (err) =>
        showToast({
          message: err.response?.data?.message || 'Failed to update preset',
          severity: 'error'
        })
    }
  );
  const setSmartfloMutation = useMutation(
    ({ userId, body }) => adminAPI.setUserSmartfloAssignment(userId, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['user-smartflo']);
        queryClient.invalidateQueries(['smartflo-c2c-tokens']);
      },
      onError: (err) =>
        showToast({
          message: err.response?.data?.message || 'Failed to save Smartflo assignment',
          severity: 'error'
        })
    }
  );

  const apiPayload = data?.data?.data || data?.data || {};
  const users = apiPayload.users || [];
  const pagination = apiPayload.pagination || {};
  const totalPages = pagination.totalPages || 1;

  const validPackageIds = useMemo(
    () => new Set(leadPackages.map((p) => String(p.id))),
    [leadPackages]
  );

  const openEdit = (user) => {
    setEditingUser(user);
    setEditForm({
      role: user.role || 'user',
      isActive: !!user.isActive
    });
    setHandlesLeads(false);
    setLeadTrekIds([]);
    setLeadPackageIds([]);
    setLeadFromDate('');
    setLeadToDate('');
    setLeadAllocMode('calendar');
    setLeadAllocPresetId('');
    setConsultantTrekIds([]);
    setSmartfloTokenId('');
  };

  useEffect(() => {
    const alloc = leadAllocationData?.data?.data;
    if (!alloc || !editingUser?.id) return;
    setHandlesLeads(Boolean(alloc.handlesLeads));
    setLeadTrekIds((alloc.trekIds || []).map(String));
    setLeadPackageIds((alloc.packageIds || []).map(String));
    setLeadFromDate(alloc.fromDate ? String(alloc.fromDate).slice(0, 10) : '');
    setLeadToDate(alloc.toDate ? String(alloc.toDate).slice(0, 10) : '');
    setLeadAllocMode(alloc.mode === 'preset' ? 'preset' : 'calendar');
    setLeadAllocPresetId(alloc.presetId != null ? String(alloc.presetId) : '');
  }, [leadAllocationData, editingUser?.id]);

  useEffect(() => {
    const payload = consultantTreksData?.data?.data;
    if (!payload || !editingUser?.id) return;
    setConsultantTrekIds((payload.trekIds || []).map(String));
  }, [consultantTreksData, editingUser?.id]);

  useEffect(() => {
    const alloc = userSmartfloData?.data?.data;
    if (!alloc || !editingUser?.id) return;
    setSmartfloTokenId(alloc.tokenId ? String(alloc.tokenId) : '');
  }, [userSmartfloData, editingUser?.id]);

  useEffect(() => {
    if (!handlesLeads) return;
    setLeadPackageIds((prev) => prev.filter((id) => validPackageIds.has(String(id))));
  }, [handlesLeads, validPackageIds]);

  const handleSaveEdit = async () => {
    if (!editingUser?.id) return;
    if (handlesLeads && !leadTrekIds.length) {
      showToast({ message: 'Select at least one trek for lead allocation.', severity: 'warning' });
      return;
    }
    if (handlesLeads && leadAllocMode === 'preset' && !leadAllocPresetId) {
      showToast({ message: 'Select a date preset or switch to calendar mode.', severity: 'warning' });
      return;
    }
    try {
      await updateMutation.mutateAsync({
        id: editingUser.id,
        payload: {
          role: editForm.role,
          permissions: [],
          isActive: editForm.isActive
        }
      });
      await setLeadAllocationMutation.mutateAsync({
        userId: editingUser.id,
        body: {
          handlesLeads,
          trekIds: handlesLeads ? leadTrekIds.map(Number) : [],
          packageIds: handlesLeads ? leadPackageIds.map(Number) : [],
          mode: handlesLeads ? leadAllocMode : 'calendar',
          presetId:
            handlesLeads && leadAllocMode === 'preset' && leadAllocPresetId
              ? Number(leadAllocPresetId)
              : null,
          fromDate:
            handlesLeads && leadAllocMode === 'calendar' ? leadFromDate || null : null,
          toDate: handlesLeads && leadAllocMode === 'calendar' ? leadToDate || null : null
        }
      });
      await setConsultantTreksMutation.mutateAsync({
        userId: editingUser.id,
        trekIds: consultantTrekIds.map(Number)
      });
      await setSmartfloMutation.mutateAsync({
        userId: editingUser.id,
        body: { tokenId: smartfloTokenId ? Number(smartfloTokenId) : null }
      });
      setEditingUser(null);
      showToast({ message: 'User updated successfully.', severity: 'success' });
    } catch {
      // handled by mutation error callbacks
    }
  };

  return (
    <div className="module-page users-page-module">
      <PageHeader
        title="Users"
        subtitle="Manage admin users, roles, lead allocation, and trek consultants."
        actions={
          isSuperAdmin ? (
            <button
              type="button"
              className="module-btn secondary"
              onClick={() => setPresetDialogOpen(true)}
            >
              Lead date presets
            </button>
          ) : null
        }
      />

      <FilterBar>
        <div className="users-search-wrap">
          <Search size={18} className="users-search-icon" />
          <input
            type="text"
            placeholder="Search by name, email, phone..."
            value={search}
            onChange={(e) => setQuery({ search: e.target.value })}
            className="users-search-input"
          />
        </div>
        <div className="users-filters">
          <select className="module-filter-select" value={roleFilter} onChange={(e) => setQuery({ role: e.target.value })}>
            <option value="">All roles</option>
            {roleNames.map((roleName) => (
              <option key={roleName} value={roleName}>{roleName}</option>
            ))}
          </select>
          <select className="module-filter-select" value={activeFilter} onChange={(e) => setQuery({ isActive: e.target.value })}>
            <option value="">All status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>
      </FilterBar>

      <div className="module-card">
        {isLoading ? (
          <p className="users-loading">Loading users...</p>
        ) : error ? (
          <p className="users-error">Failed to load users.</p>
        ) : users.length === 0 ? (
          <EmptyState
            title="No users found"
            description="Try adjusting your search or filters."
          />
        ) : (
          <>
            <table className="module-table users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email / Phone</th>
                  <th>Role</th>
                  <th>Permissions</th>
                  <th>Status</th>
                  <th>Last Login</th>
                  {isSuperAdmin && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <strong>{user.firstName} {user.lastName}</strong>
                    </td>
                    <td>
                      <span>{user.email || '—'}</span>
                      {user.phone && <span className="users-phone">{user.phone}</span>}
                    </td>
                    <td>
                      <span className={`users-role-badge ${user.role}`}>
                        {user.role === 'super_admin' ? <Shield size={14} /> : <User size={14} />}
                        {user.role === 'super_admin' ? 'Super Admin' : user.role}
                      </span>
                    </td>
                    <td>
                      {user.role === 'super_admin' ? (
                        <span className="users-perms-all">Full access</span>
                      ) : (
                        <span className="users-perms-count">
                          {Array.isArray(user.permissions) && user.permissions.length ? `${user.permissions.length} rights` : 'None'}
                        </span>
                      )}
                    </td>
                    <td>
                      <StatusChip
                        status={user.isActive ? 'active' : 'inactive'}
                        label={user.isActive ? 'Active' : 'Inactive'}
                      />
                    </td>
                    <td>{user.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'Never'}</td>
                    {isSuperAdmin && (
                      <td>
                        {user.role !== 'super_admin' && (
                          <>
                            <button
                              type="button"
                              className="users-edit-btn"
                              onClick={() => openEdit(user)}
                              title="Edit role & lead allocation"
                            >
                              <Pencil size={16} />
                              Edit
                            </button>
                            <button
                              type="button"
                              className="users-edit-btn users-delete-btn"
                              onClick={() => {
                                if (window.confirm('Delete this admin user? This cannot be undone.')) {
                                  deleteUserMutation.mutate(user.id);
                                }
                              }}
                              title="Delete user"
                            >
                              <Trash2 size={16} />
                              Delete
                            </button>
                          </>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {pagination.total > limit && (
              <div className="users-pagination">
                <span>Showing {(page - 1) * limit + 1}–{Math.min(page * limit, pagination.total)} of {pagination.total}</span>
                <div className="users-pagination-btns">
                  <button type="button" className="module-btn secondary" disabled={page <= 1} onClick={() => setQuery({ page: String(page - 1) }, { resetPage: false })}>Previous</button>
                  <span>Page {page} of {totalPages}</span>
                  <button type="button" className="module-btn secondary" disabled={page >= totalPages} onClick={() => setQuery({ page: String(page + 1) }, { resetPage: false })}>Next</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Dialog
        open={Boolean(presetDialogOpen && isSuperAdmin)}
        onClose={() => setPresetDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Lead date presets</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Rolling windows for lead allocation (e.g. last 5 days). Deactivate presets instead of deleting.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="flex-start" sx={{ mb: 2.5 }}>
            <TextField
              label="New preset label"
              size="small"
              fullWidth
              value={newPresetLabel}
              onChange={(e) => setNewPresetLabel(e.target.value)}
              placeholder="e.g. Last 7 days"
            />
            <TextField
              label="Days"
              size="small"
              type="number"
              value={newPresetDays}
              onChange={(e) => setNewPresetDays(e.target.value)}
              placeholder="7"
              inputProps={{ min: 1, max: 3650 }}
              sx={{ width: { xs: '100%', sm: 120 }, flexShrink: 0 }}
            />
            <Button
              variant="contained"
              disabled={createPresetMutation.isLoading || !newPresetLabel.trim() || !newPresetDays}
              onClick={() =>
                createPresetMutation.mutate({
                  label: newPresetLabel.trim(),
                  days: Number(newPresetDays)
                })
              }
              sx={{ height: 40, whiteSpace: 'nowrap' }}
            >
              {createPresetMutation.isLoading ? 'Adding…' : 'Add preset'}
            </Button>
          </Stack>

          <table className="module-table users-table">
            <thead>
              <tr>
                <th>Label</th>
                <th>Days</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {leadPresets.length === 0 ? (
                <tr>
                  <td colSpan={4}>No presets yet.</td>
                </tr>
              ) : (
                leadPresets.map((p) => (
                  <tr key={p.id}>
                    <td>{p.label}</td>
                    <td>{p.days}</td>
                    <td>{p.isActive !== false ? 'Active' : 'Inactive'}</td>
                    <td>
                      {p.isActive !== false ? (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={updatePresetMutation.isLoading}
                          onClick={() => {
                            if (window.confirm(`Deactivate "${p.label}"?`)) {
                              updatePresetMutation.mutate({ id: p.id, body: { isActive: false } });
                            }
                          }}
                        >
                          Deactivate
                        </Button>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="outlined" onClick={() => setPresetDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(editingUser)}
        onClose={() => setEditingUser(null)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Edit user: {editingUser?.firstName} {editingUser?.lastName}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ pt: 0.5 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2
              }}
            >
              <FormControl fullWidth size="small">
                <InputLabel id="edit-user-role-label">Role</InputLabel>
                <Select
                  labelId="edit-user-role-label"
                  label="Role"
                  value={editForm.role}
                  onChange={(e) => setEditForm((p) => ({ ...p, role: e.target.value }))}
                >
                  {roleNames.filter((r) => r !== 'super_admin').map((roleName) => (
                    <MenuItem key={roleName} value={roleName}>{roleName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel id="edit-user-active-label">Status</InputLabel>
                <Select
                  labelId="edit-user-active-label"
                  label="Status"
                  value={String(editForm.isActive)}
                  onChange={(e) => setEditForm((p) => ({ ...p, isActive: e.target.value === 'true' }))}
                >
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: `1px solid ${brand.border}`,
                bgcolor: '#f8fafc'
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={handlesLeads}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setHandlesLeads(checked);
                      if (!checked) {
                        setLeadTrekIds([]);
                        setLeadPackageIds([]);
                        setLeadFromDate('');
                        setLeadToDate('');
                        setLeadAllocMode('calendar');
                        setLeadAllocPresetId('');
                      }
                    }}
                    sx={{ color: brand.primary, '&.Mui-checked': { color: brand.primary } }}
                  />
                }
                label={<Typography fontWeight={700}>Leads</Typography>}
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4.5, mt: -0.5 }}>
                When enabled, this user only sees lead requests for the treks below.
                When disabled, they see none. Also requires the Lead Requests view permission on their role (Role Builder).
                Trek consultant (Hey, Traveler! card) is assigned separately below.
              </Typography>

              {handlesLeads ? (
                <Stack spacing={2} sx={{ mt: 2 }}>
                  {leadAllocationLoading ? (
                    <Typography variant="body2" color="text.secondary">Loading lead allocation…</Typography>
                  ) : null}

                  <FormControl fullWidth size="small">
                    <InputLabel id="user-lead-trek-label">Trek</InputLabel>
                    <Select
                      labelId="user-lead-trek-label"
                      multiple
                      value={leadTrekIds}
                      label="Trek"
                      onChange={(e) => {
                        const next = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                        setLeadTrekIds([...new Set(next.map(String))]);
                      }}
                      renderValue={(selected) =>
                        selected.length <= 2
                          ? selected
                              .map((id) => trekOptions.find((t) => String(t.id) === String(id))?.name || id)
                              .join(', ')
                          : `${selected.length} treks selected`
                      }
                      MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
                    >
                      {trekOptions.map((t) => (
                        <MenuItem key={t.id} value={String(t.id)}>
                          <Checkbox checked={leadTrekIds.includes(String(t.id))} size="small" sx={{ py: 0, mr: 0.5 }} />
                          <ListItemText primary={t.name} primaryTypographyProps={{ variant: 'body2' }} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl
                    fullWidth
                    size="small"
                    disabled={!leadTrekIds.length || leadOptionsLoading}
                  >
                    <InputLabel id="user-lead-package-label">Package (optional)</InputLabel>
                    <Select
                      labelId="user-lead-package-label"
                      multiple
                      value={leadPackageIds}
                      label="Package (optional)"
                      onChange={(e) => {
                        const next = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                        setLeadPackageIds([...new Set(next.map(String))]);
                      }}
                      renderValue={(selected) => {
                        if (!selected.length) {
                          return <span style={{ color: '#94a3b8' }}>All packages</span>;
                        }
                        if (selected.length <= 2) {
                          return selected
                            .map((id) => {
                              const pkg = leadPackages.find((p) => String(p.id) === String(id));
                              return pkg?.name || id;
                            })
                            .join(', ');
                        }
                        return `${selected.length} packages selected`;
                      }}
                      MenuProps={{ PaperProps: { style: { maxHeight: 360, minWidth: 320 } } }}
                    >
                      {leadPackages.map((pkg) => (
                        <MenuItem key={pkg.id} value={String(pkg.id)} sx={{ alignItems: 'flex-start', py: 1 }}>
                          <Checkbox
                            checked={leadPackageIds.includes(String(pkg.id))}
                            size="small"
                            sx={{ py: 0, mr: 0.5, mt: 0.15 }}
                          />
                          <ListItemText
                            primary={pkg.name}
                            secondary={packageDetailSummary(pkg) || null}
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                            secondaryTypographyProps={{ variant: 'caption', sx: { whiteSpace: 'normal' } }}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                      Lead date filter (optional)
                    </Typography>
                    <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                      <InputLabel id="user-lead-alloc-mode-label">Date mode</InputLabel>
                      <Select
                        labelId="user-lead-alloc-mode-label"
                        label="Date mode"
                        value={leadAllocMode}
                        onChange={(e) => {
                          const next = e.target.value;
                          setLeadAllocMode(next);
                          if (next === 'preset') {
                            setLeadFromDate('');
                            setLeadToDate('');
                          } else {
                            setLeadAllocPresetId('');
                          }
                        }}
                      >
                        <MenuItem value="calendar">Calendar range</MenuItem>
                        <MenuItem value="preset">Rolling preset</MenuItem>
                      </Select>
                    </FormControl>

                    {leadAllocMode === 'preset' ? (
                      <FormControl fullWidth size="small">
                        <InputLabel id="user-lead-alloc-preset-label">Date preset</InputLabel>
                        <Select
                          labelId="user-lead-alloc-preset-label"
                          label="Date preset"
                          value={leadAllocPresetId}
                          onChange={(e) => setLeadAllocPresetId(e.target.value)}
                          displayEmpty
                          renderValue={(selected) => {
                            if (!selected) {
                              return <span style={{ color: '#94a3b8' }}>Select preset…</span>;
                            }
                            const hit = activeLeadPresets.find((p) => String(p.id) === String(selected));
                            return hit ? `${hit.label} (${hit.days} days)` : selected;
                          }}
                        >
                          <MenuItem value="">
                            <em>Select preset…</em>
                          </MenuItem>
                          {activeLeadPresets.map((p) => (
                            <MenuItem key={p.id} value={String(p.id)}>
                              {p.label} ({p.days} days)
                            </MenuItem>
                          ))}
                        </Select>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                          Shows leads created within the last N days (rolling window). Manage presets via Lead date presets on this page.
                        </Typography>
                      </FormControl>
                    ) : (
                      <>
                        <AdminDateRangeFilter
                          startDate={leadFromDate}
                          endDate={leadToDate}
                          onStartDateChange={setLeadFromDate}
                          onEndDateChange={setLeadToDate}
                          startLabel="From"
                          endLabel="To"
                          autoOpenEndAfterStart={false}
                        />
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                          Leave blank for all event dates. When set, batch-tied leads (registrations, callbacks with a date) must fall in this range. PDF and other trek-only leads still show for the selected treks.
                        </Typography>
                      </>
                    )}
                  </Box>
                </Stack>
              ) : null}
            </Box>

            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: `1px solid ${brand.border}`,
                bgcolor: '#fff'
              }}
            >
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                Trek consultant
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                Shows this user on the event page &quot;Hey, Traveler!&quot; card for the selected treks.
                One trek can have only one consultant — saving here replaces any previous assignment.
              </Typography>
              {consultantTreksLoading ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Loading consultant treks…
                </Typography>
              ) : null}
              <FormControl fullWidth size="small">
                <InputLabel id="user-consultant-trek-label">Consultant treks</InputLabel>
                <Select
                  labelId="user-consultant-trek-label"
                  multiple
                  value={consultantTrekIds}
                  label="Consultant treks"
                  onChange={(e) => {
                    const next = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                    setConsultantTrekIds([...new Set(next.map(String))]);
                  }}
                  renderValue={(selected) =>
                    !selected.length
                      ? 'None'
                      : selected.length <= 2
                        ? selected
                            .map((id) => trekOptions.find((t) => String(t.id) === String(id))?.name || id)
                            .join(', ')
                        : `${selected.length} treks selected`
                  }
                  MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
                >
                  {trekOptions.map((t) => (
                    <MenuItem key={t.id} value={String(t.id)}>
                      <Checkbox
                        checked={consultantTrekIds.includes(String(t.id))}
                        size="small"
                        sx={{ py: 0, mr: 0.5 }}
                      />
                      <ListItemText primary={t.name} primaryTypographyProps={{ variant: 'body2' }} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Divider />

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={700}>
                  Smartflo click-to-call number
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => refetchSmartfloTokens()}
                  disabled={smartfloTokensLoading}
                >
                  {smartfloTokensLoading ? 'Syncing…' : 'Refresh numbers'}
                </Button>
              </Box>
              {userSmartfloLoading ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Loading Smartflo assignment…
                </Typography>
              ) : null}
              <FormControl fullWidth size="small">
                <InputLabel id="user-smartflo-token-label">Outbound number</InputLabel>
                <Select
                  labelId="user-smartflo-token-label"
                  label="Outbound number"
                  value={smartfloTokenId}
                  onChange={(e) => setSmartfloTokenId(e.target.value)}
                  disabled={smartfloTokensLoading}
                  MenuProps={{ PaperProps: { style: { maxHeight: 420 } } }}
                >
                  <MenuItem value="">
                    <em>Not assigned</em>
                  </MenuItem>
                  {smartfloTokenOptions.map((token) => {
                    const takenByOther =
                      token.assignedUserId &&
                      String(token.assignedUserId) !== String(editingUser?.id);
                    const suffix = takenByOther ? ` (used by ${token.assignedUserName})` : '';
                    return (
                      <MenuItem
                        key={token.id}
                        value={String(token.id)}
                        disabled={Boolean(takenByOther)}
                      >
                        {token.label}{suffix}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                {smartfloTokenOptions.length
                  ? `${smartfloTokenOptions.length} enabled number${smartfloTokenOptions.length === 1 ? '' : 's'} available.`
                  : 'No numbers synced yet.'}{' '}
                Numbers are synced from Tata Smartflo. Assign one per user for Lead Requests click-to-call.
              </Typography>
              {smartfloTokensData?.data?.sync?.synced != null ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                  Last sync: {smartfloTokensData.data.sync.synced} from {smartfloTokensData.data.sync.source || '—'}
                  {smartfloTokensData.data.sync.total != null
                    ? ` (${smartfloTokensData.data.sync.total} enabled in DB)`
                    : ''}
                </Typography>
              ) : null}
              {smartfloTokensData?.data?.sync?.message && !smartfloTokenOptions.length ? (
                <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
                  {smartfloTokensData.data.sync.message}
                  {' '}
                  Save tokens in <code>backend/config/smartflo-c2c-tokens.json</code> and restart PM2,
                  or set <code>SMARTFLO_C2C_SYNC_JSON_FILE</code> in .env.
                </Typography>
              ) : null}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="outlined" onClick={() => setEditingUser(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={
              updateMutation.isLoading ||
              setLeadAllocationMutation.isLoading ||
              setConsultantTreksMutation.isLoading ||
              setSmartfloMutation.isLoading ||
              leadAllocationLoading ||
              consultantTreksLoading ||
              userSmartfloLoading
            }
          >
            {(updateMutation.isLoading ||
              setLeadAllocationMutation.isLoading ||
              setConsultantTreksMutation.isLoading ||
              setSmartfloMutation.isLoading)
              ? 'Saving…'
              : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Users;
